/**
 * Фабрика для создания типизированных ошибок
 */

import { type AppError, ErrorCode } from './types';
import i18next from 'i18next';

/**
 * Создает типизированную ошибку приложения
 */
export function createAppError(
  code: ErrorCode,
  message: string,
  originalError?: unknown,
  details?: Record<string, unknown>
): AppError {
  const error = new Error(message) as AppError;
  error.code = code;
  error.originalError = originalError;
  error.details = details;
  return error;
}

/** Сетевая ошибка */
export function createNetworkError(originalError?: unknown): AppError {
  return createAppError(
    ErrorCode.NETWORK_ERROR,
    i18next.t('common:errors.network'),
    originalError
  );
}

/** Ошибка таймаута */
export function createTimeoutError(originalError?: unknown): AppError {
  return createAppError(
    ErrorCode.TIMEOUT,
    i18next.t('common:errors.timeout'),
    originalError
  );
}

/** Ошибка аутентификации */
export function createUnauthorizedError(originalError?: unknown): AppError {
  return createAppError(
    ErrorCode.UNAUTHORIZED,
    i18next.t('common:errors.notAuthenticated'),
    originalError
  );
}

/** Ошибка доступа (RLS) */
export function createForbiddenError(message?: string, originalError?: unknown): AppError {
  return createAppError(
    ErrorCode.FORBIDDEN,
    message || i18next.t('common:errors.forbidden'),
    originalError
  );
}

/** Ошибка "не найдено" */
export function createNotFoundError(resource: string, originalError?: unknown): AppError {
  return createAppError(
    ErrorCode.NOT_FOUND,
    i18next.t('common:errors.notFound', { resource }),
    originalError
  );
}

/** Ошибка валидации */
export function createValidationError(
  message: string,
  originalError?: unknown,
  details?: Record<string, unknown>
): AppError {
  return createAppError(
    ErrorCode.VALIDATION_ERROR,
    message,
    originalError,
    details
  );
}

/** Ошибка загрузки файла (слишком большой) */
export function createFileTooLargeError(maxSize: string, originalError?: unknown): AppError {
  return createAppError(
    ErrorCode.FILE_TOO_LARGE,
    i18next.t('common:errors.fileTooLarge', { maxSize }),
    originalError
  );
}

/** Ошибка загрузки файла (неверный формат) */
export function createInvalidFileTypeError(
  allowedTypes: string[],
  originalError?: unknown
): AppError {
  return createAppError(
    ErrorCode.INVALID_FILE_TYPE,
    i18next.t('common:errors.invalidFileType', { types: allowedTypes.join(', ') }),
    originalError
  );
}

/** Ошибка превышения квоты хранилища */
export function createStorageQuotaError(originalError?: unknown): AppError {
  return createAppError(
    ErrorCode.STORAGE_QUOTA_EXCEEDED,
    i18next.t('common:errors.storageQuota'),
    originalError
  );
}

/** Ошибка базы данных */
export function createDatabaseError(message?: string, originalError?: unknown): AppError {
  return createAppError(
    ErrorCode.DATABASE_ERROR,
    message || i18next.t('common:errors.database'),
    originalError
  );
}

/** Неизвестная ошибка */
export function createUnknownError(originalError?: unknown): AppError {
  return createAppError(
    ErrorCode.UNKNOWN,
    i18next.t('common:errors.unknown'),
    originalError
  );
}